import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { Link } from "@/components/ui/link";
import { useParams } from "@/lib/navigation";
import * as React from "react";

import { AttachCapabilityDialog } from "@/components/authoring/attach-capability-dialog";
import { AttachKbDialog } from "@/components/authoring/attach-kb-dialog";
import { GenerationParamsCard } from "@/components/authoring/generation-params";
import { KbStatusBadge } from "@/components/authoring/kb-status-badge";
import { ComparePromoteCard } from "@/components/authoring/live-version";
import { LlmConfigCard } from "@/components/authoring/llm-config-card";
import { SkillDialog } from "@/components/authoring/skill-dialog";
import { GuardrailsCard } from "@/components/authoring/guardrails-card";
import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import {
  deleteSkill,
  detachCapability,
  detachKb,
  updateAttachment,
  updateVersion,
  type VersionInput,
} from "@/lib/api/endpoints";
import { useAttached, useAttachedKbs, useSkills, useVersion } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { AgentStatus, AttachmentView, DeliveryMode, SkillView } from "@/types/api";

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const STATUSES: AgentStatus[] = [
  "draft",
  "evaluated",
  "commissioned",
  "active",
  "suspended",
  "retired",
];
const DELIVERY_MODES: DeliveryMode[] = ["executable", "rules_export", "external"];

export default function VersionBuilderPage() {
  const params = useParams();
  const slug = String(params.slug);
  const label = String(params.label);
  const qc = useQueryClient();
  const version = useVersion(slug, label);
  const skills = useSkills(slug, label);

  // Config form — hydrated from the loaded version.
  const [form, setForm] = React.useState<VersionInput>({});
  const loadedFor = React.useRef<number | null>(null);
  React.useEffect(() => {
    const v = version.data;
    if (v && loadedFor.current !== v.id) {
      loadedFor.current = v.id;
      setForm({
        tag: v.tag,
        soul: v.soul,
        instructions: v.instructions,
        chat_instructions: v.chat_instructions,
        supports_chat: v.supports_chat,
        skills_in_chat: v.skills_in_chat,
        tools_on_demand: v.tools_on_demand,
        delivery_mode: v.delivery_mode as DeliveryMode,
        status: v.status as AgentStatus,
      });
    }
  }, [version.data]);

  const save = useMutation({
    mutationFn: () => updateVersion(slug, label, { ...form, tag: form.tag || null }),
    onSuccess: async (v) => {
      toast.success("Version saved.");
      await qc.invalidateQueries({ queryKey: qk.version(slug, label) });
      await qc.invalidateQueries({ queryKey: qk.versions(slug) });
      loadedFor.current = null; // allow re-hydrate (tag/label may have changed)
      version.refetch();
      void v;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const set = <K extends keyof VersionInput>(key: K, value: VersionInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Skills sub-editor
  const [skillOpen, setSkillOpen] = React.useState(false);
  const [editSkill, setEditSkill] = React.useState<SkillView | undefined>();
  const [toDeleteSkill, setToDeleteSkill] = React.useState<SkillView | undefined>();
  const delSkill = useMutation({
    mutationFn: (skillSlug: string) => deleteSkill(slug, label, skillSlug),
    onSuccess: async () => {
      toast.success("Skill removed.");
      await qc.invalidateQueries({ queryKey: qk.skills(slug, label) });
      await qc.invalidateQueries({ queryKey: qk.version(slug, label) });
      setToDeleteSkill(undefined);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Tools (capabilities) sub-editor
  const attached = useAttached(slug, label);
  const [attachOpen, setAttachOpen] = React.useState(false);
  const toggleApproval = useMutation({
    mutationFn: (a: AttachmentView) =>
      updateAttachment(slug, label, a.tool_name, { requires_approval: !a.requires_approval }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.attached(slug, label) });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const detach = useMutation({
    mutationFn: (toolName: string) => detachCapability(slug, label, toolName),
    onSuccess: async () => {
      toast.success("Tool detached.");
      await qc.invalidateQueries({ queryKey: qk.attached(slug, label) });
      await qc.invalidateQueries({ queryKey: qk.version(slug, label) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Knowledge bases sub-editor
  const attachedKbs = useAttachedKbs(slug, label);
  const [attachKbOpen, setAttachKbOpen] = React.useState(false);
  const detachKbM = useMutation({
    mutationFn: (kbSlug: string) => detachKb(slug, label, kbSlug),
    onSuccess: async () => {
      toast.success("Knowledge base detached.");
      await qc.invalidateQueries({ queryKey: qk.attachedKbs(slug, label) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (version.isError) {
    return <ErrorState error={version.error} onRetry={() => version.refetch()} />;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Link
        href={`/agents/${slug}`}
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Versions
      </Link>
      <PageHeader
        title={label}
        description={`Configure this version of “${slug}”.`}
        actions={
          version.data?.tag ? <Badge variant="secondary">{version.data.tag}</Badge> : undefined
        }
      />

      {version.isLoading || !version.data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="space-y-6">
          <ComparePromoteCard agentSlug={slug} label={label} status={version.data.status} />
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Runtime configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tag">Tag</Label>
                  <Input
                    id="tag"
                    value={form.tag ?? ""}
                    onChange={(e) => set("tag", e.target.value)}
                    placeholder="latest"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    className={selectClass}
                    value={form.status ?? "draft"}
                    onChange={(e) => set("status", e.target.value as AgentStatus)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="delivery">Delivery mode</Label>
                  <select
                    id="delivery"
                    className={selectClass}
                    value={form.delivery_mode ?? "executable"}
                    onChange={(e) => set("delivery_mode", e.target.value as DeliveryMode)}
                  >
                    {DELIVERY_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input accent-primary"
                  checked={form.supports_chat ?? false}
                  onChange={(e) => set("supports_chat", e.target.checked)}
                />
                Supports chat
              </label>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                  checked={form.skills_in_chat ?? false}
                  onChange={(e) => set("skills_in_chat", e.target.checked)}
                />
                <span>
                  Expose skills as tools in chat
                  <span className="block text-xs text-muted-foreground">
                    The model can call a skill mid-conversation to load its instructions. Off by
                    default — this changes how the agent answers on the live /api/v1 chat path.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                  checked={form.tools_on_demand ?? false}
                  onChange={(e) => set("tools_on_demand", e.target.checked)}
                />
                <span>
                  Load tool definitions on demand
                  <span className="block text-xs text-muted-foreground">
                    Sends tool names only and lets the model pull full schemas when it needs them.
                    Tool schemas are most of a typical prompt; this trades that for one extra round
                    trip on turns that actually use a tool.
                  </span>
                </span>
              </label>

              <div className="space-y-1.5">
                <Label htmlFor="soul">Identity</Label>
                <Textarea
                  id="soul"
                  rows={3}
                  value={form.soul ?? ""}
                  onChange={(e) => set("soul", e.target.value)}
                  placeholder="Personality / voice injected into the system prompt."
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="instructions">Prompt Instructions</Label>
                <Textarea
                  id="instructions"
                  rows={5}
                  value={form.instructions ?? ""}
                  onChange={(e) => set("instructions", e.target.value)}
                  placeholder="Task-specific behaviour."
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="chat">Communication Instructions</Label>
                <Textarea
                  id="chat"
                  rows={3}
                  value={form.chat_instructions ?? ""}
                  onChange={(e) => set("chat_instructions", e.target.value)}
                  placeholder="Extra rules that apply in chat mode only."
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save configuration"}
                </Button>
              </div>
            </CardContent>
          </Card>

          <LlmConfigCard agentSlug={slug} label={label} />

          <GenerationParamsCard
            agentSlug={slug}
            label={label}
            current={version.data.generation_params}
          />

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Skills</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditSkill(undefined);
                  setSkillOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Add skill
              </Button>
            </CardHeader>
            <CardContent>
              {skills.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (skills.data ?? []).length === 0 ? (
                <EmptyState
                  title="No skills"
                  hint="Add a structured invocation for this version."
                />
              ) : (
                <div className="divide-y divide-border">
                  {(skills.data ?? []).map((s) => (
                    <div key={s.id} className="flex items-center justify-between py-2">
                      <div>
                        <div className="text-sm font-medium">{s.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{s.slug}</div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit skill"
                          onClick={() => {
                            setEditSkill(s);
                            setSkillOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete skill"
                          onClick={() => setToDeleteSkill(s)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Tools</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setAttachOpen(true)}>
                <Plus className="h-4 w-4" />
                Attach tool
              </Button>
            </CardHeader>
            <CardContent>
              {attached.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (attached.data ?? []).length === 0 ? (
                <EmptyState
                  title="No tools attached"
                  hint="Attach an MCP or HTTP tool this version can call."
                />
              ) : (
                <div className="divide-y divide-border">
                  {(attached.data ?? []).map((a) => (
                    <div key={a.capability_id} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{a.tool_name}</span>
                        <Badge variant="outline" className="font-mono text-[10px]">
                          {a.execution_type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-input accent-primary"
                            checked={a.requires_approval}
                            disabled={toggleApproval.isPending}
                            onChange={() => toggleApproval.mutate(a)}
                          />
                          Approval
                        </label>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Detach tool"
                          onClick={() => detach.mutate(a.tool_name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Knowledge bases</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setAttachKbOpen(true)}>
                <Plus className="h-4 w-4" />
                Attach knowledge base
              </Button>
            </CardHeader>
            <CardContent>
              {attachedKbs.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (attachedKbs.data ?? []).length === 0 ? (
                <EmptyState
                  title="No knowledge bases attached"
                  hint="Attach a SQL knowledge base this version can ground its answers in."
                />
              ) : (
                <div className="divide-y divide-border">
                  {(attachedKbs.data ?? []).map((kb) => (
                    <div
                      key={kb.knowledge_base_id}
                      className="flex items-center justify-between py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Link
                          href={`/knowledge-bases/${kb.slug}`}
                          className="text-sm font-medium hover:underline"
                        >
                          {kb.name}
                        </Link>
                        <KbStatusBadge status={kb.status} />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Detach knowledge base"
                        onClick={() => detachKbM.mutate(kb.slug)}
                        disabled={detachKbM.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <GuardrailsCard agentSlug={slug} label={label} />
        </div>
      )}

      <AttachCapabilityDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        agentSlug={slug}
        label={label}
        attachedToolNames={(attached.data ?? []).map((a) => a.tool_name)}
      />
      <AttachKbDialog
        open={attachKbOpen}
        onOpenChange={setAttachKbOpen}
        agentSlug={slug}
        label={label}
        attachedSlugs={(attachedKbs.data ?? []).map((k) => k.slug)}
      />
      <SkillDialog
        open={skillOpen}
        onOpenChange={setSkillOpen}
        agentSlug={slug}
        label={label}
        skill={editSkill}
      />
      <ConfirmDialog
        open={!!toDeleteSkill}
        onOpenChange={(o) => !o && setToDeleteSkill(undefined)}
        title={`Remove skill “${toDeleteSkill?.name}”?`}
        description="This cannot be undone."
        confirmLabel="Remove"
        pending={delSkill.isPending}
        onConfirm={() => toDeleteSkill && delSkill.mutate(toDeleteSkill.slug)}
      />
    </div>
  );
}
